import {io} from 'socket.io-client';
import Raven from 'raven-js';

const RECONNECTION_DELAY = 1000;
const RECONNECTION_DELAY_MAX = 10000;
const CONNECTION_TIMEOUT = 20000;

const SERVER_DISCONNECT_RETRY_DELAY = 2000;

export const SocketStatus = {
    CONNECTED: 'connected',
    CONNECTING: 'connecting',
    DISCONNECTED: 'disconnected',
};

const breadcrumb = (message, level = 'info') => {
    try {
        if (Raven.isSetup()) {
            Raven.captureBreadcrumb({category: 'socket', message, level});
        }
    } catch (e) {
        // ignore
    }
};

class SocketService {

    constructor() {
        this.socket = null;
        this.url = null;
        this.status = SocketStatus.DISCONNECTED;

        this.hasConnected = false;

        this.messageHandlers = {};
        this.reconnectHandlers = [];
        this.statusHandlers = [];

        this.serverDisconnectTimeout = null;
        this.managerHandlers = null;

        this.handleBrowserOnline = this.handleBrowserOnline.bind(this);
        this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    }


    connect(url) {
        if (!url || url === '') return;

        if (this.socket && this.url === url) {
            this.ensureConnected();
            return;
        }

        if (this.socket) this.disconnect();

        this.url = url;
        this.setStatus(SocketStatus.CONNECTING);

        this.socket = io(url, {
            // Leave transports at their default order: polling first, then
            // upgrade. Websocket only fails outright behind proxies that block it.
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: RECONNECTION_DELAY,
            reconnectionDelayMax: RECONNECTION_DELAY_MAX,
            randomizationFactor: 0.5,
            timeout: CONNECTION_TIMEOUT,
        });

        this.bindSocketEvents();
        this.bindBrowserEvents();
    }


    disconnect() {
        this.unbindBrowserEvents();
        this.clearServerDisconnectTimeout();

        if (this.socket) {
            // Not covered by removeAllListeners(), and socket.io caches a
            // Manager per url, so these would stack up on a logout/login cycle.
            this.unbindManagerEvents();
            this.socket.removeAllListeners();
            this.socket.disconnect();
            this.socket = null;
        }

        this.url = null;
        this.hasConnected = false;
        this.setStatus(SocketStatus.DISCONNECTED);
    }


    isConnected() {
        return !!this.socket && this.socket.connected;
    }


    getStatus() {
        return this.status;
    }


    // Handlers live here rather than on the socket, so they survive it being
    // torn down and rebuilt. Returns an unsubscribe function.
    on(event, handler) {
        if (!this.messageHandlers[event]) {
            this.messageHandlers[event] = [];

            if (this.socket) this.bindMessageEvent(event);
        }

        this.messageHandlers[event].push(handler);

        return () => {
            this.messageHandlers[event] = this.messageHandlers[event].filter(h => h !== handler);
        };
    }


    // Fires when the connection is restored, never on the first connect.
    onReconnect(handler) {
        this.reconnectHandlers.push(handler);

        return () => {
            this.reconnectHandlers = this.reconnectHandlers.filter(h => h !== handler);
        };
    }


    // Fires with the current status immediately, and on every change after.
    onStatusChange(handler) {
        this.statusHandlers.push(handler);
        handler(this.status);

        return () => {
            this.statusHandlers = this.statusHandlers.filter(h => h !== handler);
        };
    }


    bindSocketEvents() {
        const socket = this.socket;

        socket.on('connect', () => {
            const reconnected = this.hasConnected;

            this.hasConnected = true;
            this.clearServerDisconnectTimeout();
            this.setStatus(SocketStatus.CONNECTED);

            breadcrumb(reconnected ? 'reconnected' : 'connected');

            if (reconnected) {
                this.reconnectHandlers.forEach(handler => {
                    try {
                        handler();
                    } catch (e) {
                        Raven.captureException(e);
                    }
                });
            }
        });

        socket.on('disconnect', (reason) => {
            this.setStatus(SocketStatus.DISCONNECTED);
            breadcrumb(`disconnected: ${reason}`, 'warning');

            // The one case socket.io will not retry by itself.
            if (reason === 'io server disconnect') {
                this.clearServerDisconnectTimeout();
                this.serverDisconnectTimeout = setTimeout(() => {
                    this.serverDisconnectTimeout = null;
                    this.ensureConnected();
                }, SERVER_DISCONNECT_RETRY_DELAY);
            }
        });

        socket.on('connect_error', (error) => {
            this.setStatus(SocketStatus.DISCONNECTED);
            breadcrumb(`connect_error: ${error && error.message}`, 'warning');
        });

        // Reconnection is driven by the manager, not the socket, since v3.
        this.managerHandlers = {
            reconnect_attempt: (attempt) => {
                this.setStatus(SocketStatus.CONNECTING);
                breadcrumb(`reconnect_attempt ${attempt}`);
            },
            error: (error) => {
                breadcrumb(`manager error: ${error && error.message}`, 'warning');
            },
        };

        Object.keys(this.managerHandlers).forEach(event => {
            socket.io.on(event, this.managerHandlers[event]);
        });

        Object.keys(this.messageHandlers).forEach(event => this.bindMessageEvent(event));
    }


    unbindManagerEvents() {
        if (!this.socket || !this.managerHandlers) return;

        Object.keys(this.managerHandlers).forEach(event => {
            this.socket.io.off(event, this.managerHandlers[event]);
        });

        this.managerHandlers = null;
    }


    bindMessageEvent(event) {
        this.socket.on(event, (...args) => {
            (this.messageHandlers[event] || []).forEach(handler => {
                try {
                    handler(...args);
                } catch (e) {
                    Raven.captureException(e);
                }
            });
        });
    }


    bindBrowserEvents() {
        if (typeof window === 'undefined') return;

        window.addEventListener('online', this.handleBrowserOnline);
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }


    unbindBrowserEvents() {
        if (typeof window === 'undefined') return;

        window.removeEventListener('online', this.handleBrowserOnline);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }


    handleBrowserOnline() {
        breadcrumb('browser reports online');
        this.ensureConnected();
    }


    handleVisibilityChange() {
        // A backgrounded tab is where the socket usually dies without socket.io
        // noticing, so recover here instead of waiting for the ping timeout.
        if (document.visibilityState === 'visible') {
            this.ensureConnected();
        }
    }


    ensureConnected() {
        if (!this.socket || this.socket.connected) return;

        this.setStatus(SocketStatus.CONNECTING);
        this.socket.connect();
    }


    clearServerDisconnectTimeout() {
        if (this.serverDisconnectTimeout) {
            clearTimeout(this.serverDisconnectTimeout);
            this.serverDisconnectTimeout = null;
        }
    }


    setStatus(status) {
        if (this.status === status) return;

        this.status = status;
        this.statusHandlers.forEach(handler => {
            try {
                handler(status);
            } catch (e) {
                Raven.captureException(e);
            }
        });
    }
}

export default new SocketService();
